import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  Send,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './CircularsPage.css';

const CircularsPage = () => {
  const { school } = useAuth();
  const whatsappEnabled = !!school?.whatsappEnabled;

  // State management
  const [circulars, setCirculars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [acknowledgeId, setAcknowledgeId] = useState(null);
  const [waBusy, setWaBusy] = useState(null);
  const [waToast, setWaToast] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'circular',
    targetAudience: 'all',
    priority: 'normal',
    expiryDate: '',
    requiresAcknowledgement: false,
    broadcastWhatsApp: false,
  });

  const typeColors = {
    notice: '#3b82f6',
    circular: '#10b981',
    memo: '#f472b6',
    alert: '#dc2626',
  };

  const typeBgColors = {
    notice: '#dbeafe',
    circular: '#d1fae5',
    memo: '#fbcfe8',
    alert: '#fee2e2',
  };

  const priorityColors = {
    urgent: '#dc2626',
    high: '#f472b6',
    normal: '#3b82f6',
    low: '#6b7280',
  };

  const priorityBgColors = {
    urgent: '#fee2e2',
    high: '#fbcfe8',
    normal: '#dbeafe',
    low: '#f3f4f6',
  };

  // Fetch circulars
  useEffect(() => {
    fetchCirculars();
  }, []);

  const fetchCirculars = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/circulars');
      // Backend returns { circulars: [...], pagination }; older code also
      // tolerated a bare array or { items } shape.
      const data = response.data.circulars || response.data.items || response.data;
      setCirculars(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching circulars:', err);
      setCirculars([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCircular = () => {
    setEditingId(null);
    setFormData({
      title: '',
      content: '',
      type: 'circular',
      targetAudience: 'all',
      priority: 'normal',
      expiryDate: '',
      requiresAcknowledgement: false,
      broadcastWhatsApp: false,
    });
    setShowModal(true);
  };

  const handleEditCircular = (circular) => {
    setEditingId(circular.id);
    setFormData({
      title: circular.title || '',
      content: circular.content || '',
      type: circular.type || 'circular',
      targetAudience: circular.targetAudience || 'all',
      priority: circular.priority || 'normal',
      expiryDate: circular.expiryDate || '',
      requiresAcknowledgement: circular.requiresAcknowledgement || false,
      broadcastWhatsApp: false,
    });
    setShowModal(true);
  };

  // Re-trigger a WhatsApp broadcast for an existing circular. Used when admin
  // forgot the checkbox at create time, or wants to resend after editing.
  const handleResendWhatsApp = async (circularId) => {
    setWaBusy(circularId);
    setWaToast(null);
    try {
      const { data } = await api.post(`/circulars/${circularId}/whatsapp`);
      if (data.skipped) {
        setWaToast({ type: 'warn', text: data.reason || 'WhatsApp not configured for this school.' });
      } else {
        setWaToast({ type: 'ok', text: `Sent to ${data.sent} parents (${data.failed} failed)` });
      }
    } catch (err) {
      setWaToast({ type: 'err', text: err?.response?.data?.message || 'Send failed' });
    } finally {
      setWaBusy(null);
      setTimeout(() => setWaToast(null), 6000);
    }
  };

  const handleDeleteCircular = async (circularId) => {
    try {
      await api.delete(`/circulars/${circularId}`);
      setCirculars(circulars.filter((c) => c.id !== circularId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting circular:', err);
      setError('Failed to delete circular');
    }
  };

  const handleAcknowledge = async (circularId) => {
    try {
      await api.post(`/circulars/${circularId}/acknowledge`, {});
      fetchCirculars();
      setAcknowledgeId(null);
    } catch (err) {
      console.error('Error acknowledging circular:', err);
      setError('Failed to acknowledge circular');
    }
  };

  const handleSaveCircular = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData };

      if (editingId) {
        await api.put(`/circulars/${editingId}`, payload);
      } else {
        await api.post('/circulars', payload);
      }

      setShowModal(false);
      fetchCirculars();
    } catch (err) {
      console.error('Error saving circular:', err);
      setError('Failed to save circular');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredCirculars = circulars.filter((circular) => {
    const typeMatch = typeFilter === 'all' || circular.type === typeFilter;
    const audienceMatch = audienceFilter === 'all' || circular.targetAudience === audienceFilter;
    return typeMatch && audienceMatch;
  });

  const CircularCard = ({ circular }) => {
    const typeBg = typeBgColors[circular.type] || typeBgColors.circular;
    const typeColor = typeColors[circular.type] || typeColors.circular;
    const priorityBg = priorityBgColors[circular.priority] || priorityBgColors.normal;
    const priorityColor = priorityColors[circular.priority] || priorityColors.normal;

    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '20px',
          marginBottom: '16px',
          borderLeft: `4px solid ${typeColor}`,
          transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {circular.title}
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>
              {circular.content.substring(0, 150)}
              {circular.content.length > 150 ? '...' : ''}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
            {circular.requiresAcknowledgement && !circular.acknowledged && (
              <button
                onClick={() => setAcknowledgeId(circular.id)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                }}
              >
                <CheckCircle size={14} />
                Acknowledge
              </button>
            )}
            {whatsappEnabled && (
              <button
                onClick={() => handleResendWhatsApp(circular.id)}
                disabled={waBusy === circular.id}
                title="Send (or resend) this circular on WhatsApp"
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '4px',
                  cursor: waBusy === circular.id ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#065f46',
                  fontSize: 12,
                  fontWeight: 600
                }}
              >
                {waBusy === circular.id ? <Send size={14} /> : <MessageCircle size={14} />}
                WhatsApp
              </button>
            )}
            <button
              onClick={() => handleEditCircular(circular)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => setDeleteConfirm(circular.id)}
              style={{
                padding: '6px 10px',
                backgroundColor: '#fee2e2',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Trash2 size={16} style={{ color: '#dc2626' }} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span
            style={{
              backgroundColor: typeBg,
              color: typeColor,
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              textTransform: 'capitalize',
              fontWeight: '500',
            }}
          >
            {circular.type}
          </span>

          <span
            style={{
              backgroundColor: priorityBg,
              color: priorityColor,
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              textTransform: 'capitalize',
              fontWeight: '500',
            }}
          >
            {circular.priority}
          </span>

          <span
            style={{
              backgroundColor: '#ede9fe',
              color: '#b8860b',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              textTransform: 'capitalize',
              fontWeight: '500',
            }}
          >
            {circular.targetAudience}
          </span>

          {circular.acknowledged && (
            <span
              style={{
                backgroundColor: '#d1fae5',
                color: '#10b981',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              Acknowledged
            </span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
            {circular.issuedBy && `By: ${circular.issuedBy}`}
          </span>
          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
            <span>Issued: {circular.issuedDate ? formatDate(circular.issuedDate) : '-'}</span>
            {circular.expiryDate && <span>Expires: {formatDate(circular.expiryDate)}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Circulars & Notices
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Send official communications to school community</p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* WhatsApp broadcast toast */}
        {waToast && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              fontWeight: 500,
              ...(waToast.type === 'ok'   && { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46' }),
              ...(waToast.type === 'warn' && { background: '#fef3c7', border: '1px solid #fde68a', color: '#854d0e' }),
              ...(waToast.type === 'err'  && { background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626' }),
            }}
          >
            <MessageCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
            {waToast.text}
          </div>
        )}

        {/* Filter Bar */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Types</option>
            <option value="notice">Notice</option>
            <option value="circular">Circular</option>
            <option value="memo">Memo</option>
            <option value="alert">Alert</option>
          </select>

          <select
            value={audienceFilter}
            onChange={(e) => setAudienceFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="all">All Audiences</option>
            <option value="staff">Staff</option>
            <option value="students">Students</option>
            <option value="parents">Parents</option>
            <option value="all">All</option>
          </select>

          <button
            onClick={handleAddCircular}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: 'auto',
            }}
          >
            <Plus size={18} />
            New Circular
          </button>
        </div>

        {/* Circulars Display */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading circulars...
            </div>
          ) : filteredCirculars.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6b7280',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
              }}
            >
              <FileText size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No circulars found</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {typeFilter !== 'all' || audienceFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first circular to get started'}
              </p>
            </div>
          ) : (
            <div>
              {filteredCirculars.map((circular) => (
                <CircularCard key={circular.id} circular={circular} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Circular?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this circular?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCircular(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledge Modal */}
      {acknowledgeId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setAcknowledgeId(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Acknowledge Circular
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                By acknowledging this circular, you confirm that you have read and understood it.
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setAcknowledgeId(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleAcknowledge(acknowledgeId)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Circular Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Circular' : 'New Circular'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveCircular}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="Enter circular title"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Content *
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleFormChange}
                    placeholder="Enter circular content"
                    rows="6"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Type
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="notice">Notice</option>
                      <option value="circular">Circular</option>
                      <option value="memo">Memo</option>
                      <option value="alert">Alert</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Target Audience
                    </label>
                    <select
                      name="targetAudience"
                      value={formData.targetAudience}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="all">All</option>
                      <option value="staff">Staff</option>
                      <option value="students">Students</option>
                      <option value="parents">Parents</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="requiresAcknowledgement"
                      checked={formData.requiresAcknowledgement}
                      onChange={handleFormChange}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Requires Acknowledgement</span>
                  </label>
                </div>

                {/* WhatsApp broadcast — only at create time, only when school
                    has the feature enabled by the platform owner. */}
                {!editingId && whatsappEnabled && (
                  <div style={{ marginBottom: '16px', padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="broadcastWhatsApp"
                        checked={formData.broadcastWhatsApp}
                        onChange={handleFormChange}
                        style={{ cursor: 'pointer', marginTop: 3 }}
                      />
                      <span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <MessageCircle size={14} /> Also send via WhatsApp
                        </span>
                        <span style={{ display: 'block', fontSize: 12, color: '#047857', marginTop: 2 }}>
                          Sends a templated message to every parent's phone. Costs apply per the school's plan.
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CircularsPage;
